import type { Customer } from 'src/types/types';

import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import { Button } from '@mui/material';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';

import { useAppSelector } from 'src/hooks';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { SearchNotFound } from 'src/components/search-not-found';

import { CreateEditCustomer } from './create-customer';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  title?: string;
  onClose: () => void;
  action?: React.ReactNode;
  selected: (selectedId: string) => boolean;
  onSelect: (address: Customer | null) => void;
};

export function AddressListDialog({
  open,
  action,
  onClose,
  selected,
  onSelect,
  title = 'Address book',
}: Props) {
  const [searchAddress, setSearchAddress] = useState('');

  const customers = useAppSelector((state) => state.customers.customers);

  const sortedByPrimary = [...customers].sort((a, b) => {
    if (a.primary && !b.primary) return -1;
    if (!a.primary && b.primary) return 1;
    return a.name.localeCompare(b.name);
  });

  const dataFiltered = applyFilter({ inputData: sortedByPrimary, query: searchAddress });

  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);

  const notFound = !dataFiltered.length && !!searchAddress;

  const [createState, setCreateState] = useState(false);

  const handleSearchAddress = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchAddress(event.target.value);
  }, []);

  const handleSelectAddress = useCallback(
    (address: Customer | null) => {
      onSelect(address);
      setSearchAddress('');
      onClose();
    },
    [onClose, onSelect]
  );

  // New: handle edit button click
  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer(customer);
    setCreateState(true);
  };

  const renderList = () => (
    <Scrollbar sx={{ p: 0.5, maxHeight: 480 }}>
      {dataFiltered.map((address) => (
        <ButtonBase
          key={address.id}
          onClick={() => handleSelectAddress(address)}
          sx={{
            py: 1,
            my: 0.5,
            px: 1.5,
            gap: 0.5,
            width: 1,
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            ...(selected(`${address.id}`) && { bgcolor: 'action.selected' }),
            position: 'relative',
          }}
        >
          <Box sx={{ gap: 1, display: 'flex', alignItems: 'center', width: '100%' }}>
            <Typography variant="subtitle2" textAlign="start" sx={{ flexGrow: 1 }}>
              {address.name}
            </Typography>

            {address.primary && <Label color="info">Primær</Label>}

            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditCustomer(address);
              }}
              sx={{ ml: 1 }}
              variant="outlined"
            >
              Rediger
            </Button>
          </Box>

          {address.company && (
            <Box sx={{ color: 'primary.main', typography: 'caption' }}>
              {`${address.company} (${address.companyCvr})`}
            </Box>
          )}

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {address.address}
          </Typography>

          {address.phone && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {address.phone}
            </Typography>
          )}
        </ButtonBase>
      ))}
    </Scrollbar>
  );

  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={onClose}>
      {createState ? (
        <CreateEditCustomer
          onClose={() => {
            setCreateState(false);
            setEditingCustomer(null);
          }}
          onCreateCallback={(customer) => {
            handleSelectAddress(customer);
            setEditingCustomer(null);
            setCreateState(false);
          }}
          editingCustomer={editingCustomer}
        />
      ) : (
        <>
          <Box
            sx={{
              p: 3,
              pr: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6">{title}</Typography>

            <Button
              onClick={() => {
                setEditingCustomer(null);
                setCreateState(true);
              }}
              size="small"
              startIcon={<Iconify icon="mingcute:add-line" />}
              sx={{ alignSelf: 'flex-end' }}
            >
              Ny kunde
            </Button>
          </Box>

          <Stack sx={{ p: 2, pt: 0 }}>
            <TextField
              value={searchAddress}
              onChange={handleSearchAddress}
              placeholder="Søg..."
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Stack>

          {notFound ? (
            <SearchNotFound query={searchAddress} sx={{ px: 3, pt: 5, pb: 10 }} />
          ) : (
            renderList()
          )}
        </>
      )}
    </Dialog>
  );
}

// ----------------------------------------------------------------------

type ApplyFilterProps = {
  query: string;
  inputData: Customer[];
};

function applyFilter({ inputData, query }: ApplyFilterProps) {
  if (!query) {
    return inputData;
  }

  return inputData.filter(({ name, company, address, phone, companyCvr }) =>
    [name, company, address, phone, companyCvr?.toString()].some((field) =>
      field?.toLowerCase().includes(query.toLowerCase())
    )
  );
}
